import { useMemo } from "react";
import {
  useGetFulfillmentSettingsQuery,
  useGetStationsQuery,
} from "../store/api";

const TIP_PRESET_LIMIT = 3;
const DEFAULT_TIP_PERCENTAGES = [15, 18, 20];

const normalizeTipPercentages = (values, fallback = []) => {
  const candidates = [];

  const pushCandidate = (value) => {
    const parsed = Number(String(value || "").trim());
    if (!Number.isFinite(parsed)) return;
    if (!Number.isInteger(parsed)) return;
    if (parsed <= 0) return;
    if (candidates.includes(parsed)) return;
    candidates.push(parsed);
  };

  if (Array.isArray(values)) {
    values.forEach(pushCandidate);
  } else if (values && typeof values === "object") {
    Object.values(values).forEach(pushCandidate);
  }

  const normalized = candidates.slice(0, TIP_PRESET_LIMIT);
  if (normalized.length > 0) return normalized;

  const fallbackCandidates = [];
  const pushFallbackCandidate = (value) => {
    const parsed = Number(String(value || "").trim());
    if (!Number.isFinite(parsed)) return;
    if (!Number.isInteger(parsed)) return;
    if (parsed <= 0) return;
    if (fallbackCandidates.includes(parsed)) return;
    fallbackCandidates.push(parsed);
  };

  (Array.isArray(fallback) ? fallback : []).forEach(pushFallbackCandidate);
  return fallbackCandidates.slice(0, TIP_PRESET_LIMIT);
};

const normalizeTipConfig = (settings) => {
  const explicitTipConfig = settings?.tipConfig ?? settings?.tip_config ?? null;
  const hasExplicitTipConfig =
    settings &&
    (Object.prototype.hasOwnProperty.call(settings, "tipConfig") ||
      Object.prototype.hasOwnProperty.call(settings, "tip_config"));

  const enabled = Boolean(explicitTipConfig?.enabled ?? true);

  const percentagesSource = Array.isArray(explicitTipConfig?.percentages)
    ? explicitTipConfig.percentages
    : [];

  const percentages = normalizeTipPercentages(
    percentagesSource,
    hasExplicitTipConfig && enabled ? [] : enabled ? DEFAULT_TIP_PERCENTAGES : [],
  );

  return {
    enabled,
    percentages,
  };
};

export const useStationFeature = () => {
  const {
    data: fulfillmentSettings,
    isLoading: settingsLoading,
    isError: settingsError,
    error: settingsErrorData,
    refetch: refetchSettings,
  } = useGetFulfillmentSettingsQuery();
  const {
    data: stationsData,
    isLoading: stationsLoading,
    isError: stationsError,
    error: stationsErrorData,
    refetch: refetchStations,
  } = useGetStationsQuery();

  const stations = Array.isArray(stationsData) ? stationsData : [];
  const activeStations = stations.filter((station) => station.isActive);
  const printTokensByStation = Boolean(
    fulfillmentSettings?.printTokensByStation,
  );
  const kitchenDisplayUseStations = Boolean(
    fulfillmentSettings?.kitchenDisplayUseStations,
  );
  const orderDisplayUseStations = Boolean(
    fulfillmentSettings?.orderDisplayUseStations,
  );
  const showReadyOnly = Boolean(fulfillmentSettings?.showReadyOnly);
  const getFlagValue = (value, fallback = true) => {
    if (value === undefined || value === null) return fallback;
    return Boolean(value);
  };
  const kitchenShowCustomerName = getFlagValue(
    fulfillmentSettings?.kitchenShowCustomerName ??
      fulfillmentSettings?.kitchen_show_customer_name,
    true,
  );
  const kitchenShowCustomerPhone = getFlagValue(
    fulfillmentSettings?.kitchenShowCustomerPhone ??
      fulfillmentSettings?.kitchen_show_customer_phone,
    true,
  );
  const kitchenShowCustomerEmail = getFlagValue(
    fulfillmentSettings?.kitchenShowCustomerEmail ??
      fulfillmentSettings?.kitchen_show_customer_email,
    true,
  );
  const orderDisplayShowCustomerName = getFlagValue(
    fulfillmentSettings?.orderDisplayShowCustomerName ??
      fulfillmentSettings?.order_display_show_customer_name,
    true,
  );
  const orderDisplayShowCustomerPhone = getFlagValue(
    fulfillmentSettings?.orderDisplayShowCustomerPhone ??
      fulfillmentSettings?.order_display_show_customer_phone,
    true,
  );
  const orderDisplayShowCustomerEmail = getFlagValue(
    fulfillmentSettings?.orderDisplayShowCustomerEmail ??
      fulfillmentSettings?.order_display_show_customer_email,
    true,
  );
  const orderDisplayBackgroundUrl =
    fulfillmentSettings?.orderDisplayBackgroundUrl || "";
  const kitchenDisplayBackgroundUrl =
    fulfillmentSettings?.kitchenDisplayBackgroundUrl || "";
  const autoArchiveSeconds =
    fulfillmentSettings?.autoArchiveSeconds === null ||
    fulfillmentSettings?.autoArchiveSeconds === undefined
      ? null
      : Number(fulfillmentSettings.autoArchiveSeconds);
  const kdsMaxVisibleOrdersRaw =
    fulfillmentSettings?.kdsMaxVisibleOrders ??
    fulfillmentSettings?.kds_max_visible_orders;
  const kdsMaxVisibleOrders =
    kdsMaxVisibleOrdersRaw === null || kdsMaxVisibleOrdersRaw === undefined
      ? null
      : Number(kdsMaxVisibleOrdersRaw);
  const posPaymentMode =
    fulfillmentSettings?.posPaymentMode === "PAY_LATER"
      ? "PAY_LATER"
      : "PAY_FIRST";
  const tipConfig = useMemo(
    () => normalizeTipConfig(fulfillmentSettings),
    [fulfillmentSettings],
  );
  const qrBaseUrl = fulfillmentSettings?.qrBaseUrl || "";
  const qrPrintKioskId =
    fulfillmentSettings?.qrPrintKioskId ??
    fulfillmentSettings?.qr_print_kiosk_id ??
    null;
  const stationFeatureActive =
    activeStations.length > 0 &&
    (
      printTokensByStation ||
      kitchenDisplayUseStations ||
      orderDisplayUseStations
    );

  return {
    fulfillmentSettings,
    stations,
    activeStations,
    printTokensByStation,
    kitchenDisplayUseStations,
    orderDisplayUseStations,
    showReadyOnly,
    kitchenShowCustomerName,
    kitchenShowCustomerPhone,
    kitchenShowCustomerEmail,
    orderDisplayShowCustomerName,
    orderDisplayShowCustomerPhone,
    orderDisplayShowCustomerEmail,
    orderDisplayBackgroundUrl,
    kitchenDisplayBackgroundUrl,
    autoArchiveSeconds,
    kdsMaxVisibleOrders,
    posPaymentMode,
    tipConfig,
    qrBaseUrl,
    qrPrintKioskId,
    stationFeatureActive,
    isLoading: settingsLoading || stationsLoading,
    isError: settingsError || stationsError,
    error: settingsErrorData || stationsErrorData,
    refetchSettings,
    refetchStations,
  };
};

export default useStationFeature;
